/*
 * Sticky purchase bar (snippets/sticky-buy-bar.liquid).
 *
 * Read-only mirror of the product page: it never calculates a price and never adds to the cart itself.
 * - Price: copied from the page's live final price, in this order:
 *     set configurator total ([data-set-total]),
 *     sofa length total ([data-size-total]),
 *     dimensions total while a customization is selected ([data-summary] visible → [data-total]),
 *     otherwise the product price block (#Price-<section>).
 * - Availability: the page's stock status (#Inventory-<section>).
 * - Button: mirrors the page's own Add to cart ([data-add-to-cart]) or the set's add button ([data-set-add]),
 *   disabled state and label included. A click forwards to that real button, so every existing check (length
 *   and dimension quotes, sold out, fee lines, set lines, cart guard) still decides. When the real button is
 *   disabled, a click only scrolls to it so the customer sees why.
 * Everything is re-read on every change of the page (MutationObserver), including after a variant change
 * replaces the price, stock and buy areas, so the bar never shows an outdated price.
 */
(() => {
  if (customElements.get('sticky-buy-bar')) return;

  const text = (el) => (el ? el.textContent.replace(/\s+/g, ' ').trim() : '');
  const visible = (el) => !!el && !el.closest('[hidden]') && el.getClientRects().length > 0;

  class StickyBuyBar extends HTMLElement {
    connectedCallback() {
      this.page = this.closest('product-page') || document;
      this.sectionId = this.dataset.sectionId;
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.stockEl = this.querySelector('[data-sticky-stock]');
      this.detailEl = this.querySelector('[data-sticky-detail]');
      this.button = this.querySelector('[data-sticky-add]');
      this.label = this.querySelector('[data-sticky-label]');
      this.shown = false;
      this.passed = false;

      this.button.addEventListener('click', this.onClick.bind(this));

      this.observer = new MutationObserver((records) => {
        if (records.every((record) => this.contains(record.target))) return;
        this.schedule();
      });
      this.observer.observe(this.page, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'hidden', 'class', 'data-size-locked'],
      });
      this.page.addEventListener('input', () => this.schedule());
      this.page.addEventListener('change', () => this.schedule());

      if ('ResizeObserver' in window) {
        this.resizer = new ResizeObserver(() => {
          document.documentElement.style.setProperty('--sticky-buy-height', `${this.offsetHeight}px`);
        });
        this.resizer.observe(this);
      }

      this.watchAnchor();
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.anchorObserver?.disconnect();
      this.resizer?.disconnect();
      document.body.classList.remove('has-sticky-buy');
    }

    /* ---------- Sources on the page ---------- */

    setConfigurator() {
      return this.page.querySelector('set-configurator');
    }

    target() {
      const set = this.setConfigurator();
      if (set) return set.querySelector('[data-set-add]');
      return this.page.querySelector(`#ProductForm-${this.sectionId} [data-add-to-cart]`) || this.page.querySelector('[data-add-to-cart]');
    }

    // A stable element around the real button (the buy area is re-rendered on variant change)
    anchor() {
      const set = this.setConfigurator();
      if (set) return set.querySelector('[data-set-add]');
      return this.page.querySelector('.product__buy');
    }

    priceText() {
      const set = this.setConfigurator();
      if (set) return text(set.querySelector('[data-set-total]'));

      const size = this.page.querySelector('size-customizer [data-size-total]');
      if (size) return text(size);

      const dimension = this.page.querySelector('dimension-customizer');
      const summary = dimension?.querySelector('[data-summary]');
      if (summary && !summary.hidden) return text(summary.querySelector('[data-total]') || dimension.querySelector('[data-total]'));

      const price = document.getElementById(`Price-${this.sectionId}`);
      if (!price) return '';
      return text(price.querySelector('.price__request') || price.querySelector('.price__regular') || price.querySelector('.price'));
    }

    detailText() {
      const size = this.page.querySelector('size-customizer');
      if (size && size.classList.contains('is-custom')) return text(size.querySelector('[data-size-selected]'));
      const picker = this.page.querySelector('[data-variant-picker]');
      if (picker) {
        return Array.from(picker.querySelectorAll('[data-selected-value]')).map(text).filter(Boolean).join(' · ');
      }
      return '';
    }

    /* ---------- Mirror ---------- */

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    }

    write(el, value) {
      if (el.textContent !== value) el.textContent = value;
    }

    sync() {
      const target = this.target();
      if (!target) {
        this.toggle(false);
        return;
      }

      this.write(this.priceEl, this.priceText());

      const detail = this.detailText();
      this.write(this.detailEl, detail);
      this.detailEl.hidden = !detail;

      const stock = document.getElementById(`Inventory-${this.sectionId}`);
      const stockText = text(stock);
      this.write(this.stockEl, stockText);
      this.stockEl.hidden = !stockText;
      const dot = stock?.querySelector('.stock-dot');
      this.stockEl.dataset.state = dot ? (dot.className.match(/stock-dot--(\w+)/) || [])[1] || '' : '';

      const blocked = target.disabled || target.getAttribute('aria-disabled') === 'true';
      const busy = target.classList.contains('is-loading') || target.getAttribute('aria-busy') === 'true';
      this.button.classList.toggle('is-disabled', blocked);
      this.button.setAttribute('aria-disabled', String(blocked || busy));
      this.button.classList.toggle('is-loading', busy);
      this.write(this.label, text(target.querySelector('[data-add-to-cart-label]') || target));

      if (this.anchorEl !== this.anchor()) this.watchAnchor();
    }

    /* ---------- Visibility ---------- */

    watchAnchor() {
      this.anchorObserver?.disconnect();
      this.anchorEl = this.anchor();
      if (!this.anchorEl || !('IntersectionObserver' in window)) return;
      this.anchorObserver = new IntersectionObserver(([entry]) => {
        // Shown once the real button has scrolled away above the viewport
        this.passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.toggle(this.passed);
      });
      this.anchorObserver.observe(this.anchorEl);
    }

    toggle(show) {
      show = show && !!this.target() && visible(this.anchorEl);
      if (show === this.shown) return;
      this.shown = show;
      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
      this.setAttribute('aria-hidden', String(!show));
      document.body.classList.toggle('has-sticky-buy', show);
    }

    /* ---------- Add to cart ---------- */

    onClick() {
      const target = this.target();
      if (!target) return;
      if (target.classList.contains('is-loading') || target.getAttribute('aria-busy') === 'true') return;
      if (target.disabled || target.getAttribute('aria-disabled') === 'true') {
        const reason = this.page.querySelector('[data-size-quote]:not([hidden]), dimension-customizer [data-quote]:not([hidden])');
        (reason || this.anchorEl || target).scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      target.click();
    }
  }

  customElements.define('sticky-buy-bar', StickyBuyBar);
})();
